import type { DashboardConfigV3 } from "../../DashboardPage/redux/portfolio.types"
import type { DashboardConfigV4 } from "../../DashboardPage/redux/widgets.types"

export type ColumnConfigItem = {
  key: string
  visible: boolean
  width?: number
}

export type TabKey = string

// `columns` is optional (Task 9) so a surface with no customizable columns of
// its own — the Global Map List tab, which only ever persists `pageSize` —
// can still have an entry here without inventing an empty `columns: []`
// every other reader would then have to special-case.
export type TableConfig = Partial<
  Record<TabKey, { columns?: ColumnConfigItem[]; pageSize?: number }>
>

export type UIPreferences = {
  tables?: Record<string, TableConfig>
}

/**
 * A surface's key in the shared preference bag. Branded so every key is minted
 * through `surfaceKey`, which holds them to one shape — the bag is one flat
 * namespace shared by unrelated screens, and a stray casing reads as unset.
 */
export type SurfaceKey = string & { readonly __surfaceKey: unique symbol }

const FLAT_KEY = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/

export const surfaceKey = (key: string): SurfaceKey => {
  if (!FLAT_KEY.test(key)) {
    throw new Error(
      `"${key}" is not a surface key: use lowercase segments joined by "." or "-".`
    )
  }
  return key as SurfaceKey
}

export type SurfacePreferences = Record<string, unknown>

export type UIPreferencesResponse = {
  preferences?: SurfacePreferences | null
  list_config?: UIPreferences
  dashboard_config?: unknown
  calendar_config?: unknown
}

export type UserUIPreferencesState = {
  prefs: UIPreferences
  surfacePrefs: SurfacePreferences
  loaded: boolean
  loading: boolean
  error: string | null
}

export type DashboardConfig =
  | {
      version: 2
      order: string[]
      hidden: string[]
    }
  | Record<string, never>
  | null

export type UIPreferencesPatch =
  | { list_config: { tables?: Record<string, TableConfig> } }
  | {
      dashboard_config: DashboardConfig | DashboardConfigV3 | DashboardConfigV4
    }
  | { preferences: SurfacePreferences }
